import { useState } from "react";
import {
  Aviso,
  Botao,
  Cabecalho,
  Celula,
  CelulaCabecalho,
  Chip,
  Corpo,
  Linha,
  Tabela,
} from "../componentes/ui";
import type { EstadoDoChip } from "../componentes/ui";
import {
  ROTULO_DA_ORIGEM,
  ROTULO_DO_ESTADO_DA_EXECUCAO,
  TIPOS_DA_MIGRACAO,
  fraseDaDivergencia,
  quandoLegivel,
  resumoDaExecucao,
  temRelatorio,
  useExecucaoDaMigracao,
  useExecucoesDaMigracao,
  type ExecucaoDeMigracao,
} from "../nucleo/migracao";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { useTitulo } from "../nucleo/titulo";

function chipDaExecucao(execucao: ExecucaoDeMigracao): { estado: EstadoDoChip; texto: string } {
  if (execucao.estado === "falhou") return { estado: "erro", texto: "Falhou" };
  if (execucao.estado === "em_andamento") return { estado: "aguardando", texto: "Em andamento" };
  if (execucao.confere === false) return { estado: "atencao", texto: "Não confere" };
  return { estado: "ok", texto: ROTULO_DO_ESTADO_DA_EXECUCAO.concluida };
}

function DetalheDaExecucao({ id }: { id: string }) {
  const execucao = useExecucaoDaMigracao(id);
  if (execucao.isPending) {
    return (
      <p className="lead" role="status">
        Carregando o relatório…
      </p>
    );
  }
  if (execucao.isError) {
    return <Aviso tom="erro">Não foi possível abrir o relatório desta execução.</Aviso>;
  }
  const dados = execucao.data;
  const resumo = dados.resumo;
  return (
    <section aria-label="Relatório da execução" data-testid="relatorio-da-migracao" className="mt-4">
      <h2 className="titulo-secao">
        Relatório de {quandoLegivel(dados.iniciada_em)}
        {dados.simulacao ? " (simulação)" : ""}
      </h2>
      {dados.erro ? (
        <Aviso tom="erro" data-testid="erro-da-execucao">
          A execução parou com um erro: {dados.erro}
        </Aviso>
      ) : null}
      {dados.simulacao ? (
        <Aviso tom="marca">
          Esta foi uma simulação: os números são os de uma execução real, mas nada foi gravado.
        </Aviso>
      ) : null}
      {temRelatorio(resumo) ? (
        <>
          <Aviso
            tom={resumo.reconciliacao.confere ? "sucesso" : "atencao"}
            data-testid="reconciliacao"
          >
            {resumo.reconciliacao.confere
              ? "A reconciliação confere: tudo o que foi lido está contado e as ações batem."
              : "A reconciliação não confere: há registros sem destino ou ações faltando. Veja as divergências."}{" "}
            Ações esperadas: {resumo.reconciliacao.acoes_esperadas}; conferidas:{" "}
            {resumo.reconciliacao.acoes_conferidas}; órfãos: {resumo.reconciliacao.orfaos}.
          </Aviso>
          <Tabela legenda="Contagens por tipo" data-testid="contagens-da-migracao">
            <Cabecalho>
              <Linha>
                <CelulaCabecalho>Tipo</CelulaCabecalho>
                <CelulaCabecalho>Lidos</CelulaCabecalho>
                <CelulaCabecalho>Criados</CelulaCabecalho>
                <CelulaCabecalho>Já existiam</CelulaCabecalho>
                <CelulaCabecalho>Atualizados</CelulaCabecalho>
                <CelulaCabecalho>Sem mudança</CelulaCabecalho>
                <CelulaCabecalho>Ignorados</CelulaCabecalho>
                <CelulaCabecalho>Erros</CelulaCabecalho>
              </Linha>
            </Cabecalho>
            <Corpo>
              {TIPOS_DA_MIGRACAO.map(({ chave, rotulo }) => {
                const c = resumo.contagens[chave];
                if (!c) return null;
                return (
                  <Linha key={chave} data-testid={`contagem-${chave}`}>
                    <CelulaCabecalho scope="row">{rotulo}</CelulaCabecalho>
                    <Celula>{c.lidos}</Celula>
                    <Celula>{c.criados}</Celula>
                    <Celula>{c.reaproveitados}</Celula>
                    <Celula>{c.atualizados}</Celula>
                    <Celula>{c.inalterados}</Celula>
                    <Celula>{c.ignorados}</Celula>
                    <Celula>{c.erros}</Celula>
                  </Linha>
                );
              })}
            </Corpo>
          </Tabela>
          {resumo.anexos_pendentes > 0 ? (
            <p className="lead mt-2" data-testid="anexos-pendentes">
              {resumo.anexos_pendentes} anexo(s) registrado(s) com o download adiado.
            </p>
          ) : null}
          <h3 className="titulo-secao mt-4">Divergências</h3>
          {Object.keys(resumo.divergencias_por_codigo).length === 0 ? (
            <p className="lead" data-testid="sem-divergencias">
              Nenhuma divergência.
            </p>
          ) : (
            <>
              <ul className="list-disc pl-5" data-testid="divergencias-por-codigo">
                {Object.entries(resumo.divergencias_por_codigo).map(([codigo, total]) => (
                  <li key={codigo} data-testid={`divergencia-${codigo}`}>
                    <strong>{total}</strong> — {fraseDaDivergencia(codigo)}
                  </li>
                ))}
              </ul>
              <Tabela legenda="Registros com divergência" data-testid="divergencias-detalhadas">
                <Cabecalho>
                  <Linha>
                    <CelulaCabecalho>Registro no Movidesk</CelulaCabecalho>
                    <CelulaCabecalho>Tipo</CelulaCabecalho>
                    <CelulaCabecalho>O que aconteceu</CelulaCabecalho>
                  </Linha>
                </Cabecalho>
                <Corpo>
                  {resumo.divergencias.map((d, indice) => (
                    <Linha key={`${d.codigo}-${d.id_externo}-${indice}`}>
                      <Celula>{d.id_externo}</Celula>
                      <Celula>{d.tipo}</Celula>
                      <Celula>{fraseDaDivergencia(d.codigo)}</Celula>
                    </Linha>
                  ))}
                </Corpo>
              </Tabela>
              {resumo.divergencias_omitidas > 0 ? (
                <p className="lead mt-2">
                  Mais {resumo.divergencias_omitidas} divergência(s) não listadas aqui; os totais por
                  motivo acima as incluem.
                </p>
              ) : null}
            </>
          )}
        </>
      ) : (
        <p className="lead">Esta execução não gerou relatório.</p>
      )}
    </section>
  );
}

/** Resultado das execuções do importador do Movidesk. Somente leitura. */
export function MigracaoMovidesk() {
  useTitulo("Migração do Movidesk");
  const sessao = useSessao();
  const autorizado = sessao.data ? podeNaSessao(sessao.data, "migracao:ver") : false;
  // Sem a permissão a tela nem pergunta: o servidor negaria, e o erro seria ruído.
  const execucoes = useExecucoesDaMigracao(autorizado);
  const [aberta, setAberta] = useState<string | null>(null);

  if (!sessao.data) {
    return (
      <p className="lead" role="status">
        Verificando o seu acesso…
      </p>
    );
  }
  if (!autorizado) {
    return (
      <Aviso tom="marca" data-testid="migracao-sem-permissao">
        O resultado da migração do Movidesk é da administração do portal.
      </Aviso>
    );
  }

  return (
    <div data-testid="migracao-movidesk">
      <h1 className="titulo-pagina">Migração do Movidesk</h1>
      <p className="lead mb-4">
        O que o importador trouxe do Movidesk: organizações, pessoas, chamados e as respostas de
        cada um, com as datas originais. Aqui só se acompanha o resultado; a importação roda pela
        ferramenta do servidor e pode ser repetida sem duplicar nada.
      </p>
      {execucoes.isPending ? (
        <p className="lead" role="status">
          Carregando as execuções…
        </p>
      ) : execucoes.isError ? (
        <Aviso tom="erro">Não foi possível carregar as execuções da migração.</Aviso>
      ) : execucoes.data.length === 0 ? (
        <Aviso tom="marca" data-testid="sem-execucoes">
          Nenhuma execução ainda. Quando o importador rodar, o resultado aparece aqui.
        </Aviso>
      ) : (
        <Tabela legenda="Execuções do importador" data-testid="execucoes-da-migracao">
          <Cabecalho>
            <Linha>
              <CelulaCabecalho>Início</CelulaCabecalho>
              <CelulaCabecalho>Origem</CelulaCabecalho>
              <CelulaCabecalho>Tipo</CelulaCabecalho>
              <CelulaCabecalho>Situação</CelulaCabecalho>
              <CelulaCabecalho>Resultado</CelulaCabecalho>
              <CelulaCabecalho>
                <span className="sr-only">Relatório</span>
              </CelulaCabecalho>
            </Linha>
          </Cabecalho>
          <Corpo>
            {execucoes.data.map((execucao) => {
              const chip = chipDaExecucao(execucao);
              return (
                <Linha key={execucao.id} data-testid="execucao-da-migracao" data-id={execucao.id}>
                  <Celula>{quandoLegivel(execucao.iniciada_em)}</Celula>
                  <Celula>{ROTULO_DA_ORIGEM[execucao.origem]}</Celula>
                  <Celula>{execucao.simulacao ? "Simulação" : "Importação"}</Celula>
                  <Celula>
                    <Chip estado={chip.estado}>{chip.texto}</Chip>
                  </Celula>
                  <Celula>{resumoDaExecucao(execucao)}</Celula>
                  <Celula>
                    <Botao
                      variante="fantasma"
                      tamanho="pequeno"
                      aria-pressed={aberta === execucao.id}
                      onClick={() => setAberta(aberta === execucao.id ? null : execucao.id)}
                    >
                      {aberta === execucao.id ? "Fechar relatório" : "Ver relatório"}
                    </Botao>
                  </Celula>
                </Linha>
              );
            })}
          </Corpo>
        </Tabela>
      )}
      {aberta ? <DetalheDaExecucao id={aberta} /> : null}
    </div>
  );
}
